<template>
  <div>
  <PageHeader :title="frame ? frame.name : ' '" subtitle="Saved frame" :breadcrumbs="FRAME_CRUMBS">
    <template #title-suffix>
      <a :href="$router.resolve(FRAMES_PATH).href" class="order-first inline-flex items-center text-gray-600 hover:text-gray-900" aria-label="Back" @click.prevent="backTo($router, FRAMES_PATH)">
        <font-awesome-icon :icon="['fas', 'arrow-left']" class="w-3.5 h-3.5" />
      </a>
    </template>
  </PageHeader>
  <div class="p-6 pb-32 text-[11px]">
  <LoadingIndicator v-if="!frame && !error" message="Loading frame..." />
  <div v-else-if="error" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ error }}</div>
  <div v-else class="space-y-6">
    <section class="bg-white rounded-lg border border-gray-200 p-4 flex items-start gap-4">
      <div class="min-w-0 flex-1 space-y-1">
        <div class="text-gray-500">{{ frame.row_count.toLocaleString() }} rows · {{ frame.columns.length }} columns · saved <span :title="absolute(frame.created_at)">{{ relative(frame.created_at) }}</span><template v-if="frame.created_by_name"> · <span>{{ frame.created_by_name }}</span></template></div>
        <div v-if="frame.description" class="text-gray-700">{{ frame.description }}</div>
        <div v-if="frame.versions.length > 1" class="text-gray-500">
          Versions of this name:
          <template v-for="(v, i) in frame.versions" :key="v.id">
            <span v-if="i">, </span>
            <span v-if="v.id === frame.id" class="text-gray-900 font-medium">{{ v.id }}</span>
            <router-link v-else :to="framePath(v.id)" class="text-neutral-800">{{ v.id }}</router-link>
          </template>
        </div>
      </div>
      <button type="button" class="btn-primary-sm shrink-0" @click="download">Download CSV</button>
    </section>
    <div v-if="downloadError" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ downloadError }}</div>

    <div class="grid grid-cols-1 gap-6">
      <section class="bg-white rounded-lg border border-gray-200">
        <header class="px-4 py-3 border-b border-gray-200 text-[12px] font-semibold text-gray-900">Produced by</header>
        <div class="p-4">
          <template v-if="producer.kind === 'hermes_run'">
            <router-link :to="{ name: 'AdminHermesRun', params: { runId: producer.run_id } }" class="text-neutral-800 font-medium">{{ producer.template_name || 'Hermes run' }}</router-link>
            <div class="text-gray-500">Hermes run {{ producer.run_id }} · <span>{{ producer.executor_name }}</span> · <span :title="absolute(producer.run_at)">{{ relative(producer.run_at) }}</span></div>
          </template>
          <template v-else-if="producer.kind === 'upload'">
            <div class="text-gray-900 font-medium">{{ producer.filename }}<template v-if="producer.sheet"> · sheet {{ producer.sheet }}</template></div>
            <div class="text-gray-500">Uploaded file</div>
          </template>
          <template v-else-if="producer.kind === 'agent'">
            <router-link :to="`/agent/${producer.session_id}`" class="text-neutral-800 font-medium">{{ producer.title || 'Agent session' }}</router-link>
            <div class="text-gray-500">Saved by the agent</div>
          </template>
          <template v-else-if="producer.kind === 'sql' || producer.kind === 'kernel'">
            <router-link v-if="producer.notebook_id" :to="`/impact/notebooks/${producer.notebook_id}`" class="text-neutral-800 font-medium">Notebook {{ producer.notebook_id }}</router-link>
            <div class="text-gray-500">{{ producer.kind === 'sql' ? 'Kept from a SQL cell' : 'Saved from a Python cell' }}<template v-if="producer.cell"> · cell {{ producer.cell }}</template></div>
          </template>
          <div v-else class="text-gray-500">{{ producer.kind || 'Saved' }}</div>
        </div>
      </section>
    </div>

    <section class="bg-white rounded-lg border border-gray-200">
      <header class="px-4 py-3 border-b border-gray-200 text-[12px] font-semibold text-gray-900">Rows</header>
      <div class="p-4">
        <LoadingIndicator v-if="sampleLoading" message="Loading rows..." />
        <div v-else-if="sampleError" class="text-gray-700">{{ sampleError }}</div>
        <div v-else class="overflow-x-auto">
          <table class="border-collapse">
            <thead><tr>
              <th v-for="c in frame.columns" :key="c.name" class="px-2 py-1 text-left font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap">{{ c.name }}</th>
            </tr></thead>
            <tbody>
              <tr v-for="(row, i) in rows" :key="i" class="border-b border-gray-50">
                <td v-for="c in frame.columns" :key="c.name" class="px-2 py-1 text-gray-800 whitespace-nowrap max-w-[24rem] truncate">{{ row[c.name] }}</td>
              </tr>
            </tbody>
          </table>
          <div class="text-gray-500 mt-2">First {{ rows.length }} of {{ frame.row_count.toLocaleString() }} rows</div>
        </div>
      </div>
    </section>
  </div>
  </div>
  </div>
</template>

<script>
import axios from 'axios';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { absolute, relative } from '@/components/Admin/Hermes/hermes';
import { downloadFrame } from '@/utils/frameDownload';
import { FRAME_CRUMBS, FRAMES_PATH } from './impactNav';
import { backTo } from '@/utils/backTo';

const SAMPLE_ROWS = 100;

export default {
  name: 'FramePage',
  components: { PageHeader, LoadingIndicator },
  props: {
    frameId: { type: Number, required: true },
  },
  data() {
    return { frame: null, error: '', rows: [], sampleLoading: false, sampleError: '', downloadError: '', FRAME_CRUMBS, FRAMES_PATH };
  },
  computed: {
    producer() {
      return (this.frame && this.frame.producer) || {};
    },
  },
  watch: {
    frameId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    absolute,
    relative,
    backTo,
    framePath(id) {
      return `/impact/frames/${id}`;
    },
    async load() {
      this.error = '';
      this.rows = [];
      try {
        const { data } = await axios.get(`api/impact_analytics/frames/${this.frameId}/`);
        this.frame = data;
      } catch (err) {
        this.error = err?.response?.status === 404 ? 'No frame with that id on this tenant.' : 'The frame could not be loaded.';
        return;
      }
      this.sampleLoading = true;
      this.sampleError = '';
      try {
        const { data } = await axios.get(`api/impact_analytics/frames/${this.frameId}/sample/`, { params: { n: SAMPLE_ROWS } });
        this.rows = data.rows || [];
      } catch (err) {
        this.sampleError = err?.response?.status === 403
          ? 'Rows are patient-level data; your role does not include PHI access.'
          : 'The rows could not be loaded.';
      } finally {
        this.sampleLoading = false;
      }
    },
    async download() {
      this.downloadError = await downloadFrame(this.frame) || '';
    },
  },
};
</script>
